import { Component, Input, OnInit } from '@angular/core';
import { NgbModal, NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { StepsService } from '../../../services/steps.service';
import { Step } from '../../stepper/step';
import { PasswordRetrievalComponent } from '../password-retrieval.component';
import {
	IUserIdentificationRequest,
	UserIdentificationRequest,
	UnlockAccountAuth,
	IUserIdentificationResponse,
} from '../../../classes/unlock-account-authentication';
import {
	IInitializePasswordRequest,
	InitializePasswordRequest,
	InitializePassword,
} from '../../../classes/initialize-password';
import { ICodeService } from '../../../services/icode.service';
import { IUserData, UserData } from '../../../../globals/classes/userData';
import { PasswordValidator } from '../../../../globals/classes/password-validator';
import { ValidationService } from '../../../../globals/classes/validaton-service';
import { GoogleAnalyticsUtils } from '../../../classes/static/google-analytics-utils';
import { ComponentWithErrors } from '../../../classes/component-with-errors';
import { IUserIDRequest } from '../../../classes/http-requests';
import { ISendSmsOtpResponse } from '../../../classes/http-responses';
import { finalize } from 'rxjs/operators/finalize';
import { AppSettings } from '../../../app.settings';

@Component({
	selector: 'restore-with-authentication',
	template: require('./restore-with-authentication.component.html'),
})
export class RestoreWithAuthenticationComponent extends ComponentWithErrors implements OnInit {
	@Input() public hasBackBtn = true;
	@Input() isBlocked: boolean;
	public steps: Step[];
	public isValidForm: boolean;
	public identifyRequest: IUserIdentificationRequest = new UserIdentificationRequest();
	public initRequest: IInitializePasswordRequest = new InitializePasswordRequest();
	private userData: IUserData = new UserData();
	public needsVerification: boolean;
	public icode: string;
	private arePasswordsValid: boolean;
	public loading: boolean;
	public isLoginBigModalEnabled: boolean = AppSettings.isLoginBigModalEnabled;
	public isRebrading = AppSettings.isRebrading;

	public get hasBack() {
		if (!this.hasBackBtn) {
			return this.stepsService.getCurrentStep != 0;
		} else {
			return true;
		}
	}
	constructor(
		private modalService: NgbModal,
		public stepsService: StepsService,
		private activeModal: NgbActiveModal,
		private icodeService: ICodeService
	) {
		super();
	}

	public ngOnInit() {
		this.steps = this.stepsService.getTransactionSteps('RestoreWithAuthentication');
		this.stepsService.setStep(this.steps, 0);
		this.initSubscriptions();
		GoogleAnalyticsUtils.sendRetrievePasswordStep1();
		ValidationService.GlobalValidation.resetValidation();
		
		// Track personal details entry screen (Step 0)
		this.setDynatraceEvent('IB_PR_Enter_Personal_Details');
	}

	public closeModal(): void {
		this.activeModal.close();
	}

	public moveBackStep(steps: Step[]): void {
		if (this.stepsService.getCurrentStep === 0) {
			return this.toggleModalDialog();
		}
		this.stepsService.moveBackStep(steps);
	}

	public moveStep(): void {
		if (this.stepsService.getCurrentStep === 0) {
			this.indentifyUser();
		} else if (this.stepsService.getCurrentStep === 1) {
			if (this.needsVerification) {
				this.verifyUserIdentification();
			} else {
				this.sendInitPass();
			}
		} else if (this.stepsService.getCurrentStep === 2) {
			if (this.needsVerification) {
				this.sendInitPass();
			} else {
				this.initializePassword();
			}
		} else if (this.stepsService.getCurrentStep === 3 && this.needsVerification) {
			this.initializePassword();
		} else {
			this.handleError();
		}
	}

	public indentifyUser() {
		this.loading = true;
		UnlockAccountAuth.identifyUser(this.identifyRequest)
			.pipe(finalize(() => (this.loading = false)))
			.subscribe(
				(res) => {
					this.resolveIdentifyUserResponse(res);
				},
				(error) => {
					this.handleError();
				}
			);
	}

	private resolveIdentifyUserResponse(response: IUserIdentificationResponse) {
		if (response && response.status == 'Pending' && response.needsPhoneVerification) {
			this.needsVerification = true;
			this.steps = this.stepsService.getTransactionSteps('RestoreWithPasswordAuthentication');
			this.stepsService.moveNextStep(this.steps);
			GoogleAnalyticsUtils.sendRetrievePasswordStep2();
		} else if (response && response.status == 'Success' && !response.needsPhoneVerification) {
			this.needsVerification = false;
			this.stepsService.moveNextStep(this.steps);
			// Track password creation screen (Step 1 - no verification needed path)
			this.setDynatraceEvent('IB_PR_Personal_Create_New_Password');
		} else {
			this.handleError();
		}
	}

	public verifyUserIdentification() {
		this.loading = true;
		UnlockAccountAuth.verifyUserIdentification(this.identifyRequest)
			.pipe(finalize(() => (this.loading = false)))
			.subscribe(
				(res) => {
					this.resolveVerifyIdResponse(res);
				},
				(error) => {
					this.handleError();
				}
			);
	}

	private resolveVerifyIdResponse(response: IUserIdentificationResponse) {
		if (response && response.status == 'Success') {
			GoogleAnalyticsUtils.sendRetrievePasswordStep3();
			this.stepsService.moveNextStep(this.steps);
			// Track password creation screen (Step 2 - verification needed path)
			this.setDynatraceEvent('IB_PR_Personal_Create_New_Password');
		} else {
			this.handleError();
		}
	}

	public initializePassword() {
		this.loading = true;
		this.initRequest.otp = this.icode;
		this.initRequest.userID = this.userData.userId;
		InitializePassword.initializePassword(this.initRequest)
			.pipe(finalize(() => (this.loading = false)))
			.subscribe(
				(res) => {
					if (res.succeeded) {
						this.stepsService.moveNextStep(this.steps);
						if (this.stepsService.getCurrentStep === 2) {
							GoogleAnalyticsUtils.sendRetrievePasswordStep4();
						} else {
							GoogleAnalyticsUtils.sendRetrievePasswordStep5();
						}
						// Track success screen
						this.setDynatraceEvent('IB_PR_Personal_Success');
					} else {
						this.handleError();
					}
				},
				(error) => {
					this.handleError(error);
				}
			);
	}

	public sendInitPass(moveNextStep = true) {
		this.loading = true;
		let request: IUserIDRequest = { UserID: this.userData.userId };
		InitializePassword.SendInitOtpPass(request)
			.pipe(finalize(() => (this.loading = false)))
			.subscribe(
				(res) => {
					if (res && res.succeeded === true) {
						this.resolveSendInitPassResponse(request, res, moveNextStep);
					} else {
						this.handleError();
					}
				},
				(error) => {
					this.handleError(error);
				}
			);
	}

	private resolveSendInitPassResponse(
		request: IUserIDRequest,
		res: ISendSmsOtpResponse,
		moveNextStep = true
	) {
		this.icodeService.resetTimer();
		this.userData.hasSMSOtp = res.hasOtp;
		this.userData.maskedPhoneNumber = res.maskedPhoneNumber;
		UserData.update(this.userData);
		if (moveNextStep) {
			this.stepsService.moveNextStep(this.steps);
			// Track OTP confirmation screen
			this.setDynatraceEvent('IB_PR_Personal_Confirm_OTP');
		}
		if (this.stepsService.getCurrentStep === 1) {
			GoogleAnalyticsUtils.sendRetrievePasswordStep3();
		} else {
			GoogleAnalyticsUtils.sendRetrievePasswordStep4();
		}
	}

	public toggleModalDialog(): void {
		this.activeModal.close();
		this.modalService.open(PasswordRetrievalComponent, {
			windowClass: 'modal-small',
		});
	}

	public hideButton() {
		if (!this.needsVerification && this.stepsService.getCurrentStep === 3) {
			return true;
		} else if (this.needsVerification && this.stepsService.getCurrentStep === 4) {
			return true;
		} else {
			return false;
		}
	}

	public get canProceed(): boolean {
		switch (this.stepsService.getCurrentStep) {
			case 0:
				return this.arePersonalDetailsValid;
			case 1:
				return this.needsVerification
					? this.isVerificationCodeValid
					: this.arePasswordsValid;
			case 2:
				return this.needsVerification ? this.arePasswordsValid : this.isIcodeValid;
			case 3:
				return this.needsVerification ? this.isIcodeValid : true;
		}
	}

	private get arePersonalDetailsValid(): boolean {
		return this.isValidForm;
	}

	private get isVerificationCodeValid(): boolean {
		return (
			this.identifyRequest.verificationCode != null &&
			this.identifyRequest.verificationCode.length >= 6
		);
	}

	private get isIcodeValid(): boolean {
		return this.icodeService.isValidCode(this.icode);
	}

	private initSubscriptions() {
		this.mortalize(UserData.data$).subscribe((userData) => {
			this.userData = userData;
			this.identifyRequest.userId = userData.userId;
		});
		this.mortalize(this.icodeService.code$).subscribe((c) => (this.icode = c));
		this.mortalize(this.icodeService.codeRequested$).subscribe((c) => this.sendInitPass(false));
		this.mortalize(ValidationService.GlobalValidation.isValid).subscribe(
			(val) => (this.isValidForm = val)
		);

		this.mortalize(PasswordValidator.isValidPassAndConfirm$).subscribe(
			(val) => (this.arePasswordsValid = val)
		);
	}
}
